import { NumberFilter } from "@react3l/advanced-filters";
import { Card, Radio } from "antd";
import { BarChart } from "components/Charts/BarChart/BarChart";
import { DEVICE_TYPE_MASTER_ROUTE, END_USER_MASTER_ROUTE, HOME_CONTROLLER_MASTER_ROUTE } from "config/route-consts";
import { Dashboard, DashboardFilter } from "models/Dashboard";
import { Info } from "models/Info";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { forkJoin } from "rxjs";
import { dashboardRepository } from "repositories/dashboard-repository";
import "./DashboardMaster.scss";
import DeviceTypeBars from "./DeviceTypeBars";
import HcHealth from "./HcHealth";
import OverviewInfo from "./OverviewInfo";
import RecentEndUsers from "./RecentEndUsers";

type OverviewKey = "endUser" | "dormitory" | "homeController" | "device";
type OverviewData = Record<OverviewKey, { quantity?: number; rate?: number }>;

const TIME_OPTIONS = [
  { label: "Tháng", value: 1 },
  { label: "Quý", value: 2 },
  { label: "Năm", value: 3 },
];
const PERIOD_LABEL: Record<number, string> = { 1: "tháng trước", 2: "quý trước", 3: "năm trước" };

const TYPE_OPTIONS = [
  { label: "Số lượng", value: 1 },
  { label: "Tăng trưởng", value: 2 },
];

const STATS: { key: OverviewKey; title: string; icon: string; load: (f: DashboardFilter) => any }[] = [
  { key: "endUser", title: "Khách hàng", icon: "tio-user_outlined", load: dashboardRepository.overviewEndUser },
  { key: "dormitory", title: "Nhà", icon: "tio-home_outlined", load: dashboardRepository.overviewDormitory },
  { key: "homeController", title: "Home Controller", icon: "tio-speaker", load: dashboardRepository.overviewHomeController },
  { key: "device", title: "Thiết bị", icon: "tio-plug_outlined", load: dashboardRepository.overviewDevice },
];

function dashboardFilter(time: number, type: number): DashboardFilter {
  return { time: new NumberFilter({ equal: time }), type: new NumberFilter({ equal: type }), skip: 0, take: 1 };
}

export default function DashboardMaster() {
  const [time, setTime] = useState<number>(1);
  const [trendType, setTrendType] = useState<number>(1);
  const [overviewData, setOverviewData] = useState<Partial<OverviewData>>({});
  const [overviewLoading, setOverviewLoading] = useState<boolean>(true);

  // Mỗi ô cần cả số lượng (type 1) lẫn tỉ lệ so với kỳ trước (type 2). Đổi kỳ thì huỷ request cũ,
  // không thì kỳ cũ trả về sau sẽ đè số của kỳ mới.
  useEffect(() => {
    setOverviewLoading(true);
    const requests: Record<string, any> = {};
    STATS.forEach(({ key, load }) => {
      requests[`${key}Quantity`] = load(dashboardFilter(time, 1));
      requests[`${key}Rate`] = load(dashboardFilter(time, 2));
    });
    const subscription = forkJoin(requests).subscribe({
      next: (data: Record<string, Info>) => {
        const result: Partial<OverviewData> = {};
        STATS.forEach(({ key }) => {
          result[key] = { quantity: data[`${key}Quantity`]?.quantity, rate: data[`${key}Rate`]?.rate };
        });
        setOverviewData(result);
        setOverviewLoading(false);
      },
      error: () => setOverviewLoading(false),
    });
    return () => subscription.unsubscribe();
  }, [time]);

  const chartFilter = useMemo<Dashboard>(() => ({ time, type: trendType }), [time, trendType]);
  const periodFilter = useMemo<Dashboard>(() => ({ time, type: 1 }), [time]);

  return (
    <div className="page dashboard-master__container">
      <div className="dashboard-master__header">
        <h1 className="dashboard-master__title">Tổng quan</h1>
        <Radio.Group
          className="dashboard-master__segmented"
          optionType="button"
          options={TIME_OPTIONS}
          value={time}
          onChange={(event) => setTime(event.target.value)}
        />
      </div>

      <section className="dashboard-master__stats">
        {STATS.map(({ key, title, icon }) => (
          <OverviewInfo
            key={key}
            title={title}
            icon={icon}
            quantity={overviewData[key]?.quantity}
            rate={overviewData[key]?.rate}
            periodLabel={PERIOD_LABEL[time]}
            loading={overviewLoading}
          />
        ))}
      </section>

      <div className="dashboard-master__grid">
        <Card
          className="dashboard-master__card--hc"
          title="Tình trạng kết nối HC"
          extra={
            <Link className="dashboard-master__see-all" to={HOME_CONTROLLER_MASTER_ROUTE}>
              Xem tất cả
            </Link>
          }
        >
          <HcHealth />
        </Card>
        <Card
          className="dashboard-master__card--customer"
          title="Khách hàng mới"
          extra={
            <Link className="dashboard-master__see-all" to={END_USER_MASTER_ROUTE}>
              Xem tất cả
            </Link>
          }
        >
          <RecentEndUsers getData={dashboardRepository.listEndUser} filter={periodFilter} />
        </Card>
        <Card
          className="dashboard-master__card--trend"
          title="Xu hướng"
          extra={
            <Radio.Group
              className="dashboard-master__segmented dashboard-master__segmented--sm"
              optionType="button"
              options={TYPE_OPTIONS}
              value={trendType}
              onChange={(event) => setTrendType(event.target.value)}
            />
          }
        >
          <BarChart filter={chartFilter} loadData={dashboardRepository.overview} type="line" height={300} />
        </Card>
        <Card
          className="dashboard-master__card--device"
          title="Thiết bị theo loại"
          extra={
            <Link className="dashboard-master__see-all" to={DEVICE_TYPE_MASTER_ROUTE}>
              Xem tất cả
            </Link>
          }
        >
          <DeviceTypeBars filter={periodFilter} />
        </Card>
      </div>
    </div>
  );
}
